import Card from '@/components/elements/Card/Card';
import { Fab } from '@/components/elements/forms/buttons';
import { ListItem } from '@/components/elements/lists';
import { CompanyAvatar } from '@/components/elements/redesign/Avatar';
import Rating from '@/components/elements/redesign/Rating/Rating';
import Icon from '@/components/icons/Icon';
import { capitalizeFirstLetter } from '@/helpers';
import useMobileView from '@/hooks/useMobileView';
import { _s } from '@/locale';
import { useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Slider from 'react-slick';
import SliderWrapper from '../Slider';

type CompaniesProps = {
  companies: {
    id: number;
    to: string;
    image?: string;
    name: string;
    address: { street?: string; city?: string };
    rating: number;
    openHours?: PlaceStatus;
  }[];
  title: { normal?: string; bold: string };
  city?: string;
  bottomSlot?: React.ReactNode;
};

type PlaceStatus = {
  status: 'open' | 'openSoon' | 'closed';
  openHours: string;
};

const Companies = ({ companies, title, city, bottomSlot }: CompaniesProps) => {
  const { isMobileView } = useMobileView();
  const history = useHistory();
  const sliderRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const settings = {
    dots: false,
    infinite: false,
    speed: 400,
    slidesToShow: isMobileView ? 1.35 : 2.35,
    slidesToScroll: 1,
    arrows: false,
    draggable: true,
    swipe: companies.length > 1,
  };

  const nextSlide = () => {
    sliderRef.current.slickNext();
  };

  const prevSlide = () => {
    sliderRef.current.slickPrev();
  };

  const handleClick = (e: React.MouseEvent, to: string) => {
    if (isDragging) {
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    history.push(to);
  };

  return (
    <Card size={isMobileView ? 'lg' : 'xl'} childClassName="pr-0">
      <div className="gap-md flex flex-col">
        <div className="pr-3xl flex justify-between">
          <h2 className="text-h-s">
            <span className="font-bold">{title.bold}</span>{' '}
            <span>
              {title.normal}&nbsp;
              {city ? _s('in') + ' ' + capitalizeFirstLetter(city) : ''}
            </span>
          </h2>
          {!isMobileView && companies.length > 2 && (
            <div className="gap-md flex">
              <Fab
                icon={<Icon variant="chevron-left" color="fg_primary" />}
                onClick={prevSlide}
                size="sm"
                variant="secondary"
              />
              <Fab
                icon={<Icon variant="chevron-right" color="fg_primary" />}
                onClick={nextSlide}
                size="sm"
                variant="secondary"
              />
            </div>
          )}
        </div>

        <SliderWrapper sliderRef={sliderRef} setIsDragging={(isDragging) => setIsDragging(isDragging)}>
          <Slider ref={sliderRef} {...settings}>
            {companies.map((card) => {
              const hasRating = typeof card.rating === 'number' && card.rating >= 0;
              const formattedAddressString = `${card.address.street ? card.address.street + ', ' : ''}${
                card.address.city
              }`;
              const hasOpeningHours = Boolean(card.openHours);
              return (
                <button onClick={(e) => handleClick(e, card.to)} key={card.id}>
                  <div className="mr-md bg-surface_l2 border-black-200 p-md flex-2 gap-xs flex flex-col rounded-lg border">
                    <h2 className="text-fg_primary text-l truncate text-start">{card.name}</h2>
                    <ListItem
                      type="div"
                      underline={hasOpeningHours}
                      leftPadding={false}
                      className={`!pr-0 ${hasOpeningHours ? '!pb-xs !pt-0' : '!py-0'}`}
                      leftSlot={<CompanyAvatar variant="default" alt="" imgUrl={card.image ?? ''} size="md" />}>
                      {hasRating && <Rating avgRating={card.rating} size="md" />}
                      <div className="text-m text-fg_primary truncate">{formattedAddressString}</div>
                    </ListItem>
                    {hasOpeningHours && (
                      <div className="flex justify-between">
                        <span className="text-m text-fg_secondary">{_s('placeCard.openToday')}</span>
                        <span className="text-m text-fg_primary">
                          {card?.openHours.status === 'open' ? card?.openHours.openHours : _s('placeCard.closed')}
                        </span>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </Slider>
        </SliderWrapper>
      </div>
      {bottomSlot && bottomSlot}
    </Card>
  );
};

export default Companies;
